import * as React from "react";

/**
 * Distance (px) from the bottom within which the container is considered
 * "pinned". While pinned, streamed content keeps the view at the bottom; once
 * the user scrolls further up than this, auto-scrolling pauses so they can read
 * earlier content without being yanked back down.
 */
const NEAR_BOTTOM_PX = 48;

export interface ScrollToBottomOptions {
  /** Instant by default; pass `"smooth"` for user-initiated jumps like Jump to latest. */
  behavior?: ScrollBehavior;
}

export interface UseStickToBottomOptions {
  /**
   * When false, streamed updates no longer re-pin the container and manual
   * scrolling no longer changes the pinned state.
   *
   * Pass `false` while the same scroll container shows non-streamed content —
   * Excel and PowerPoint render citations in the shared chat scroller, so a
   * live run would drag that list to the bottom. Pin state is preserved while
   * follow is off, so switching back to chat resumes where the user left off.
   */
  followStreamedContent?: boolean;
}

export interface UseStickToBottomResult<T extends HTMLElement> {
  scrollContainerRef: React.RefCallback<T>;
  scrollToBottom: (options?: ScrollToBottomOptions) => void;
  /** Render-visible pin state for the return-to-bottom control; only updates on transitions. */
  isAtBottom: boolean;
  /** Hide the return-to-bottom affordance during an in-flight smooth jump. */
  showReturnToBottom: boolean;
}

function cancelSmoothScroll(el: HTMLElement): void {
  if (typeof el.scrollTo !== "function") return;
  el.scrollTo({ top: el.scrollTop, behavior: "auto" });
}

function isNearBottom(node: HTMLElement): boolean {
  const maxScrollTop = Math.max(0, node.scrollHeight - node.clientHeight);
  return maxScrollTop - node.scrollTop <= NEAR_BOTTOM_PX;
}

/**
 * Stick-to-bottom helper for streaming/log-style scroll containers (chat
 * thought-process pane, agent activity list, etc.).
 *
 * Attach the returned `scrollContainerRef` to the scroll container and pass the streamed
 * values as `deps`. On every dep change the container re-pins to the bottom,
 * but only while the user is already near the bottom — manual scroll-up pauses
 * the behavior and scrolling back down resumes it. `scrollToBottom` force-pins
 * imperatively (e.g. when a new turn starts).
 */
export function useStickToBottom<T extends HTMLElement>(
  deps: React.DependencyList,
  options: UseStickToBottomOptions = {},
): UseStickToBottomResult<T> {
  const { followStreamedContent = true } = options;
  const nodeRef = React.useRef<T | null>(null);
  const pinnedRef = React.useRef(true);
  const smoothJumpActiveRef = React.useRef(false);
  const wasPinnedWhenFollowPausedRef = React.useRef(true);
  const pendingSnapWhilePausedRef = React.useRef(false);
  const [isAtBottom, setIsAtBottom] = React.useState(true);
  const [isJumpingToBottom, setIsJumpingToBottom] = React.useState(false);

  // Read through a ref inside the scroll listener so toggling follow mode
  // doesn't tear down and re-attach the subscription.
  const followStreamedContentRef = React.useRef(followStreamedContent);
  followStreamedContentRef.current = followStreamedContent;

  const abortSmoothJump = React.useCallback((): void => {
    smoothJumpActiveRef.current = false;
    setIsJumpingToBottom(false);
  }, []);

  const finishSmoothJump = React.useCallback((): void => {
    if (!smoothJumpActiveRef.current) return;
    abortSmoothJump();
  }, [abortSmoothJump]);

  // `pinnedRef` is the source of truth so scroll decisions never wait on a
  // render; `isAtBottom` is only the render-visible copy of it.
  const setPinned = React.useCallback((next: boolean): void => {
    if (pinnedRef.current === next) return;
    pinnedRef.current = next;
    setIsAtBottom(next);
  }, []);

  const scrollToBottom = React.useCallback((options: ScrollToBottomOptions = {}): void => {
    const el = nodeRef.current;
    if (!el) return;
    const behavior = options.behavior ?? "auto";

    if (behavior === "smooth") {
      smoothJumpActiveRef.current = true;
      setIsJumpingToBottom(true);
      if (followStreamedContentRef.current && typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior });
      } else if (followStreamedContentRef.current) {
        el.scrollTop = el.scrollHeight;
        finishSmoothJump();
        setPinned(true);
      }
      return;
    }

    finishSmoothJump();
    if (!followStreamedContentRef.current) {
      pendingSnapWhilePausedRef.current = true;
      return;
    }
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    setPinned(true);
  }, [finishSmoothJump, setPinned]);

  // Bind on node arrival so a late-mounting container is still tracked. Stable
  // identity keeps React from detaching and re-attaching on every render.
  const scrollContainerRef = React.useCallback<React.RefCallback<T>>(
    (node) => {
      nodeRef.current = node;
      if (!node) return undefined;

      let previousScrollTop = node.scrollTop;

      const syncPinnedFromScroll = (): void => {
        if (!followStreamedContentRef.current) return;
        const nearBottom = isNearBottom(node);
        const scrolledUp = node.scrollTop < previousScrollTop - 2;

        if (smoothJumpActiveRef.current) {
          if (scrolledUp) {
            cancelSmoothScroll(node);
            finishSmoothJump();
            previousScrollTop = node.scrollTop;
            setPinned(false);
            return;
          }

          if (nearBottom) {
            finishSmoothJump();
            previousScrollTop = node.scrollTop;
            setPinned(true);
            return;
          }

          previousScrollTop = node.scrollTop;
          return;
        }

        previousScrollTop = node.scrollTop;
        setPinned(nearBottom);
      };

      const onScroll = (): void => {
        syncPinnedFromScroll();
      };

      node.addEventListener("scroll", onScroll, { passive: true });

      // A container arriving mid-stream starts at the top, so honour the pin.
      if (pinnedRef.current && followStreamedContentRef.current) {
        node.scrollTop = node.scrollHeight;
      }

      return () => {
        node.removeEventListener("scroll", onScroll);
        abortSmoothJump();
        nodeRef.current = null;
      };
    },
    [abortSmoothJump, finishSmoothJump, setPinned],
  );

  // Excel and PowerPoint share one scroll container between chat and citations.
  // While follow is off, scroll events are ignored so jump state can stick. On
  // re-enable, wait until chat content has measurable height before syncing
  // pin/button state — an immediate read often sees scrollHeight === clientHeight
  // and falsely marks the viewport as pinned.
  React.useLayoutEffect(() => {
    if (!followStreamedContent) {
      wasPinnedWhenFollowPausedRef.current = pinnedRef.current;
      pendingSnapWhilePausedRef.current = false;
      abortSmoothJump();
      return () => {};
    }

    abortSmoothJump();

    let cancelled = false;
    const syncAfterLayout = (): void => {
      if (cancelled) return;
      const el = nodeRef.current;
      if (!el) return;

      const maxScrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
      if (maxScrollTop <= 0) {
        if (pendingSnapWhilePausedRef.current) {
          pendingSnapWhilePausedRef.current = false;
          scrollToBottom();
          return;
        }
        setPinned(wasPinnedWhenFollowPausedRef.current);
        return;
      }

      if (pendingSnapWhilePausedRef.current) {
        pendingSnapWhilePausedRef.current = false;
        scrollToBottom();
        return;
      }

      if (wasPinnedWhenFollowPausedRef.current) {
        setPinned(true);
        scrollToBottom();
      } else {
        setPinned(isNearBottom(el));
      }
    };

    syncAfterLayout();
    return () => {
      cancelled = true;
    };
  }, [abortSmoothJump, followStreamedContent, scrollToBottom, setPinned]);

  // Re-pin on streamed content changes, only if the user hasn't scrolled up.
  // `deps` is a caller-provided dependency list of streamed values.
  // `followStreamedContent` is appended so turning follow back on catches up.
  React.useEffect(() => {
    if (!followStreamedContent) return;
    if (smoothJumpActiveRef.current) return;
    if (pinnedRef.current) scrollToBottom();
  }, [...deps, followStreamedContent]);

  return {
    scrollContainerRef,
    scrollToBottom,
    isAtBottom,
    showReturnToBottom: !isAtBottom && !isJumpingToBottom,
  };
};
